import { apiRoutes } from '@/constants/api-routes';
import { apiRequest } from './http';
import type {
  AudiencePreview,
  AutomationDetailView,
  AutomationView,
  ConditionSchemaView,
} from '@/types/api';

/** One rule row as the builder holds it — no id until the server assigns one. */
export interface AutomationConditionPayload {
  field: string;
  operator: string;
  values: string[];
}

export interface CreateAutomationPayload {
  name: string;
  listId: string;
  templateId: string;
  sendHour: number;
  timezone: string;
  includeSubscribersWithoutAccount?: boolean;
  maxRecipientsPerRun?: number;
  conditions?: AutomationConditionPayload[];
}

/** `conditions` is a FULL REPLACE when present, matching the API. */
export type UpdateAutomationPayload = Partial<Omit<CreateAutomationPayload, 'name'>> & {
  name?: string;
};

/**
 * Preview an UNSAVED draft by passing the fields; omit them to preview the
 * saved rule set. Either way the server compiles the same SQL a real run
 * would, so the count is not an estimate.
 */
export interface PreviewAutomationPayload {
  listId?: string;
  timezone?: string;
  includeSubscribersWithoutAccount?: boolean;
  /** Draft cap, so `cappedCount` tracks the field instead of the saved row. */
  maxRecipientsPerRun?: number;
  conditions?: AutomationConditionPayload[];
}

export const adminAutomationsService = {
  async conditionSchema(): Promise<ConditionSchemaView> {
    return apiRequest<ConditionSchemaView>(apiRoutes.adminAutomations.conditionSchema, {
      method: 'GET',
    });
  },

  async list(): Promise<AutomationView[]> {
    return apiRequest<AutomationView[]>(apiRoutes.adminAutomations.list, { method: 'GET' });
  },

  async getById(id: string): Promise<AutomationDetailView> {
    return apiRequest<AutomationDetailView>(apiRoutes.adminAutomations.one(id), { method: 'GET' });
  },

  async create(payload: CreateAutomationPayload): Promise<AutomationDetailView> {
    return apiRequest<AutomationDetailView>(apiRoutes.adminAutomations.create, {
      method: 'POST',
      body: payload,
    });
  },

  async update(id: string, payload: UpdateAutomationPayload): Promise<AutomationDetailView> {
    return apiRequest<AutomationDetailView>(apiRoutes.adminAutomations.one(id), {
      method: 'PATCH',
      body: payload,
    });
  },

  async remove(id: string): Promise<void> {
    await apiRequest<void>(apiRoutes.adminAutomations.one(id), { method: 'DELETE' });
  },

  async preview(id: string, payload: PreviewAutomationPayload = {}): Promise<AudiencePreview> {
    return apiRequest<AudiencePreview>(apiRoutes.adminAutomations.preview(id), {
      method: 'POST',
      body: payload,
    });
  },

  async setEnabled(id: string, enabled: boolean): Promise<AutomationDetailView> {
    const route = enabled
      ? apiRoutes.adminAutomations.enable(id)
      : apiRoutes.adminAutomations.disable(id);
    return apiRequest<AutomationDetailView>(route, { method: 'POST' });
  },

  async test(id: string, email: string): Promise<{ sent: boolean; messageId?: string }> {
    return apiRequest<{ sent: boolean; messageId?: string }>(apiRoutes.adminAutomations.test(id), {
      method: 'POST',
      body: { email },
    });
  },

  async runNow(id: string): Promise<{ queued: boolean }> {
    return apiRequest<{ queued: boolean }>(apiRoutes.adminAutomations.runNow(id), {
      method: 'POST',
    });
  },
};
